import React, { useEffect, useState } from 'react';
import '../solutions/Solutions.css'; // you can style it here
import { getDocs, collection } from 'firebase/firestore';
import { db } from '../../firebase'; // adjust based on your structure

// AI
// Data Center
// Central Office & Headend
// Outside plant
// FTTx & MDU
// Rural Broadband
// Wireless
// Enterprise
// Medical Imaging
// Neuroscience
// Optoelectronics
// Specialty Optics
// Optical Equipment

const slides = [
  {
    video: "/videos/solutionCentralOffice.mp4",
    poster: "/videos/solutionCentralOffice.jpg",
    heading: "Connectivity Guide to AI Data Center Terminology",
    subheading: "A glossary of terms to help demystify acronyms, terminology, and decode general AI data center language",
    buttonLink: "/support"
  }
];

function BlogAITerminology() {

const [products, setProducts] = useState([]);
const [filteredProducts, setFilteredProducts] = useState([]);
const [activeFilter, setActiveFilter] = useState('All');

const [open, setOpen] = useState(new Set());

  const toggle = (key) => {
    setOpen((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  };


const productFamilies = [
  'Fiber Cable Assemblies',
  'Fiber Panels',
  'Frames',
];

  const [industryFilter, setIndustryFilter] = useState('Central Office & Headends')

const topByFamily = productFamilies.map(famName => {
  const matchingProducts = filteredProducts.filter(
    p => (p.productFamily || '').trim() === famName
  );
  if (matchingProducts.length === 0) return null;

  const topProduct = matchingProducts.reduce((best, current) =>
    Number(current.rank ?? Infinity) < Number(best.rank ?? Infinity) ? current : best
  );

  return { family: famName, topProduct };
}).filter(Boolean); // remove nulls if a family has no products



useEffect(() => {
  const loadProducts = async () => {
    const querySnapshot = await getDocs(collection(db, 'products'));
    const allProducts = querySnapshot.docs
      .map(doc => ({ id: doc.id, ...doc.data() }))
      .filter(p => (p["Include Y or N"] || '').trim().toUpperCase() !== 'N');

    setProducts(allProducts);
    setFilteredProducts(allProducts); // Default to all
  };

  loadProducts();
}, []);

useEffect(() => {
  if (activeFilter === 'All') {
    setFilteredProducts(products);
  } else {
    setFilteredProducts(products.filter(p => p.productFamily === activeFilter));
  }
}, [activeFilter, products]);

  return (
    <div className="contact-page">
      <div className="curved-header-sol2" style={{paddingBottom: 60}}>
        <div className="datacenter-left" style={{ paddingTop: 100}}>
            <h1>{slides[0].heading}</h1>
            <p style={{ marginBottom: 40, fontSize: 20}}>{slides[0].subheading}</p>
            </div>
            <div className="datacenter-right" style={{ paddingTop: 60}}>
              <img src="/images/blog/aiTerminology.jpeg" className="datacenter-img" />
            </div>
            </div>



<div className="blog-section-1" style={{ marginTop: 30 }}>
  <p className="solution-section-1-description">
    In the data center, things are changing fast. AI and new technologies are emerging. Approaches to network design and management are evolving. And so is our language. With the pace of change happening faster than ever, it’s easy to feel behind the curve. After all, there isn’t a Duolingo or Rosetta Stone for data center-speak. To help mitigate the challenge, here is a glossary of AI data center terminology to help you unravel the language behind the evolution.
    <br />
    <br />

    <u>Accelerated Infrastructure</u> - Hardware and networking systems designed to speed up AI workflows, reduce costs, and improve efficiency.
    <br />
    <br />

    <u>Agentic AI</u> - AI systems designed to act as autonomous agents, capable of reasoning, planning, and executing tasks.
    <br />
    <br />

    <u>AI Factory</u> - A metaphor for an enterprise environment optimized to continuously produce AI models and applications.
    <br />
    <br />

    <u>AI Infrastructure</u> - The combination of hardware, networking, and software systems that support AI workloads at scale.
    <br />
    <br />

    <u>Co-Packaged Optics (CPO)</u> - Optical components integrated directly with processors to reduce latency and power consumption.
    <br />
    <br />

    <u>Data Center Interconnect (DCI)</u> - High-capacity links and equipment that connect two or more data centers for workload mobility, replication, or disaggregated AI clusters.
    <br />
    <br />

    <u>Data Science (AI Context)</u> - The discipline of processing, cleaning, and analyzing data to train AI models and extract actionable insights.
    <br />
    <br />

    <u>Edge / Regional Peering / Colocation</u> — Physical location and interconnection strategies that impact latency to data sources, DCI choices, and egress costs for AI workloads.
    <br />
    <br />

    <u>Edge Inference</u> — Running inference close to data sources (on-premises or edge devices) to reduce latency and egress costs.
    <br />
    <br />

    <u>End-of-Row (EoR) Switch</u> — A rack-group aggregation switch placed at the end of a row that aggregates multiple Top-of-Rack (ToR) switches.
    <br />
    <br />

    <u>Fabric</u> — A high-capacity, low-latency switching topology that interconnects servers, accelerators, and storage to behave as a single, cohesive network plane.
    <br />
    <br />

    <u>Full-Stack AI Solutions</u> - Integrated offerings that combine hardware, software, and AI models to accelerate enterprise adoption.
    <br />
    <br />

    <u>GPU (Graphics Processing Unit)</u> — A highly parallel processor optimized for matrix and tensor math; the dominant accelerator for both training and inference.
    <br />
    <br />

    <u>Inference</u> — Running a trained model to generate predictions or decisions on new input data; production-facing workload with strict latency and throughput requirements.
    <br />
    <br />

    <u>Large Language Models (LLMs)</u> - Advanced AI models trained on massive text datasets, capable of generating, summarizing, and understanding human language.
    <br />
    <br />

    <u>Latency</u> — Time between input arrival and model output; critical for real-time inference SLAs and edge applications.
    <br />
    <br />

    <u>Launch Velocity</u> - The speed at which AI projects move from concept to production, enabled by optimized infrastructure and workflows.
    <br />
    <br />

    <u>Leaf-Spine</u> — A two-tier switching architecture where leaf switches connect servers and spine switches provide non-blocking paths between leaves.
    <br />
    <br />

    <u>Machine Learning (ML)</u> - A subset of AI where systems learn patterns from data and improve performance without explicit programming.
    <br />
    <br />

    <u>Optical Interconnects</u> - Fiber-based networking solutions that enable high-speed data transfer between servers, clusters, and GPUs.
    <br />
    <br />

    <u>Packet Broker / TAP (Terminal Access Point) / SPAN (Switch Port Analyzer or mirror port)</u> — Devices and mechanisms used to replicate and steer traffic to monitoring, security, and capture systems without impacting the production path.
    <br />
    <br />

    <u>Packet Loss / Retransmission</u> — Lost packets force higher-layer retries that increase training jitter and slow distributed algorithms; an important metric because minimizing loss is critical.
    <br />
    <br />

    <u>Predictive Analytics</u> - The use of statistical models and machine learning to forecast future outcomes based on historical and real-time data.
    <br />
    <br />

    <u>Scale Across</u> - Connects distributed AI clusters across different buildings, campuses, or regions, creating "AI Factories" that function as a single logical entity.
    <br />
    <br />

    <u>Scale Out Back End</u> - Interconnecting large clusters across a data center, essential for massive data processing and high aggregate bandwidth.
    <br />
    <br />

    <u>Scale Out Front End</u> - Connecting CPUs and Storage to the Data Center and Customer.
    <br />
    <br />

    <u>Scale Up</u> - Enhances a single system, node, or rack by adding more powerful GPUs, high-speed memory, or storage.
    <br />
    <br />

    <u>SLO / SLA</u> — Service-level objective/agreement that defines acceptable latency, availability, and throughput targets for inference services.
    <br />
    <br />

    <u>Top-of-Rack (ToR) Switch</u> — The switch located at the top of a server rack that aggregates that rack’s server ports into the broader fabric.
  </p>
</div>


        <div className="curved-footer-solutions">
        <div className="curved-top-overlay-solutions" style={{ backgroundColor: 'white'}}></div>
        
        <h2>Would you like to hear more?</h2>
        <p>Our experts would love to hear about your systems and how we can help</p>
        <a className="connect-button" href="/support">LET’S CONNECT</a>
        </div>
    </div>
  );
}

export default BlogAITerminology;
